import { useState, useEffect, useCallback, useRef } from 'react';

// First polling pattern in this codebase (previously only a cosmetic
// setInterval existed, for the watermark's on-screen position) — kept
// conservative since this is a small-team tool, not a high-frequency app.
const POLL_INTERVAL_MS = 30000;

// `client` is the axios instance to use (API for admin/filmmaker, ReviewerAPI
// for reviewers — reviewers authenticate with a separate token, see
// ReviewerAuthContext.js). `basePath` is the audience's notification route
// prefix, e.g. '/api/admin/notifications'.
export default function useNotifications({ client, basePath, enabled = true }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const fetchUnreadCount = useCallback(() => {
    if (!enabled) return;
    client.get(`${basePath}/unread-count`).then(r => setUnreadCount(r.data.count)).catch(() => {});
  }, [client, basePath, enabled]);

  const fetchList = useCallback((page = 1) => {
    setLoading(true);
    return client.get(basePath, { params: { page, limit: 20 } })
      .then(r => { setItems(r.data.items); return r.data; })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client, basePath]);

  const markRead = useCallback((id) => {
    client.put(`${basePath}/${id}/read`).then(() => {
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    }).catch(() => {});
  }, [client, basePath]);

  const markAllRead = useCallback(() => {
    client.put(`${basePath}/read-all`).then(() => {
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }).catch(() => {});
  }, [client, basePath]);

  // "Clear" — permanently deletes, distinct from marking read.
  const deleteOne = useCallback((id) => {
    client.delete(`${basePath}/${id}`).then(() => {
      setItems(prev => {
        const target = prev.find(n => n.id === id);
        if (target && !target.is_read) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(n => n.id !== id);
      });
    }).catch(() => {});
  }, [client, basePath]);

  const clearAll = useCallback(() => {
    client.delete(basePath).then(() => {
      setItems([]);
      setUnreadCount(0);
    }).catch(() => {});
  }, [client, basePath]);

  useEffect(() => {
    if (!enabled) return;
    fetchUnreadCount();
    timerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [enabled, fetchUnreadCount]);

  return { unreadCount, items, loading, fetchList, markRead, markAllRead, deleteOne, clearAll, setUnreadCount };
}
